import React from 'react';
import { Dayjs } from 'dayjs';
import { Box, Button, Divider, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import FilterCategoryMultiSelect from '../controls/FilterCategoryMultiSelect';
import MaStatusSwitch from '../controls/MaStatusSwitch';
import { UnreadCountByCategoryResponse } from '../../models/notification/unreadCount';
import { OrganizationFilter } from '../../models/notification/organizationFilter';

interface FilterCategorieenProps {
  categoriesData: UnreadCountByCategoryResponse | null;
  selectedCategory: string | null;
  selectedSubCategory: string | null;
  organizations: OrganizationFilter[];
  selectedOrganizations: string[];
  onlyUnread: boolean;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  onSelectCategory: (category: string | null) => void;
  onSelectSubCategory: (subCategory: string | null) => void;
  onChangeOrganizations: (organizationIds: string[]) => void;
  onChangeOnlyUnread: (value: boolean) => void;
  onChangeDateFrom: (value: Dayjs | null) => void;
  onChangeDateTo: (value: Dayjs | null) => void;
  onClearAll: () => void;
  onClose: () => void;
}

const UnreadBadge: React.FC<{ count: number }> = ({ count }) => {
  if (!count) {
    return null;
  }

  return (
    <Box
      component="span"
      sx={{
        bgcolor: '#ed6c02',
        color: '#fff',
        borderRadius: '11px',
        px: 0.75,
        minWidth: 22,
        height: 20,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {count}
    </Box>
  );
};

const buttonSx = {
  justifyContent: 'flex-start',
  textAlign: 'left',
  textTransform: 'none',
  color: 'text.primary',
  borderRadius: 0,
  boxShadow: 'none',
  py: 0.5,
  '&:hover': { bgcolor: 'grey.100' },
};

// Breedte gereserveerd voor de expand/collapse icon-button, zodat labels netjes uitlijnen.
const ARROW_SLOT_WIDTH = 28;
// Extra inspringing (px) per niveau.
const INDENT_STEP = 15;
// Subniveau krijgt 6px extra t.o.v. het eerste niveau.
const SUB_LEVEL_INDENT = INDENT_STEP + 7.5;
// Vaste afstand (px) van de badge tot de rechterrand, net links van de arrow-kolom.
const BADGE_RIGHT_OFFSET = ARROW_SLOT_WIDTH + 10;
// Rechter padding van de knoppen zodat de tekst niet onder de badge/arrow loopt.
const ROW_PADDING_RIGHT = BADGE_RIGHT_OFFSET + 34;

// Badge absoluut gepositioneerd zodat alle badges exact op dezelfde plek staan,
// onafhankelijk van de padding van de onderliggende knop.
const PositionedBadge: React.FC<{ count: number }> = ({ count }) => {
  if (!count) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        right: `${BADGE_RIGHT_OFFSET}px`,
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      <UnreadBadge count={count} />
    </Box>
  );
};

const FilterCategorieen: React.FC<FilterCategorieenProps> = ({
  categoriesData,
  selectedCategory,
  selectedSubCategory,
  organizations,
  selectedOrganizations,
  onlyUnread,
  dateFrom,
  dateTo,
  onSelectCategory,
  onSelectSubCategory,
  onChangeOrganizations,
  onChangeOnlyUnread,
  onChangeDateFrom,
  onChangeDateTo,
  onClearAll,
  onClose,
}) => {
  const categories = categoriesData?.categories ?? [];
  const hasActiveFilters = !!selectedCategory || !!selectedSubCategory || selectedOrganizations.length > 0 || onlyUnread || !!dateFrom || !!dateTo;

  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});

  // Een categorie is standaard uitgeklapt wanneer hij geselecteerd is; de arrow-button kan dit overrulen.
  const isCategoryExpanded = (category: string) => expandedCategories[category] ?? (selectedCategory === category);

  const toggleCategoryExpanded = (category: string) => {
    setExpandedCategories((prev) => ({ ...prev, [category]: !isCategoryExpanded(category) }));
  };

  const organisatieItems = organizations.map(org => ({ id: org.organizationId, name: org.organizationDisplayName }));

  return (
    <div className="filter-side-panel">
      <div className="filter-side-panel-header">
        <span>Uitgebreid zoeken</span>
        <div className="filter-side-panel-header-actions">
          {hasActiveFilters && (
            <Tooltip title="Verwijder alle filters">
              <IconButton size="small" onClick={onClearAll}>
                <Icon>delete</Icon>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Sluit filter">
            <IconButton size="small" onClick={onClose}>
              <Icon>close</Icon>
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="filter-side-panel-body">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="body2">Toon alleen ongelezen</Typography>
          <Tooltip title={onlyUnread ? 'Toon alle berichten' : 'Toon alleen ongelezen berichten'}>
            <span>
              <MaStatusSwitch
                checked={onlyUnread}
                offIcon="mark_email_read"
                onIcon="mark_email_unread"
                offThumbColor="#fafafa"
                onThumbColor="#fff0d9"
                offIconColor="#9e9e9e"
                onIconColor="#ed6c02"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeOnlyUnread(event.target.checked)}
              />
            </span>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 1 }} />

        <Typography variant="overline" color="text.secondary">Categorie&euml;n</Typography>

        <Box sx={{ position: 'relative' }}>
          <Button
            fullWidth
            variant="text"
            disableElevation
            onClick={() => onSelectCategory(null)}
            sx={{ ...buttonSx, pl: `${INDENT_STEP}px !important`, pr: `${ROW_PADDING_RIGHT}px !important`, fontWeight: selectedCategory === null ? 600 : 400, bgcolor: selectedCategory === null ? 'grey.200' : 'transparent' }}
          >
            <span>Alle berichten</span>
          </Button>
          {categoriesData && <PositionedBadge count={categoriesData.unreadCount} />}
        </Box>

        {categories.map((category) => {
          const isSelected = selectedCategory === category.category;
          const hasSubs = category.subCategories.length > 0;
          const expanded = isCategoryExpanded(category.category);

          return (
            <React.Fragment key={category.category}>
              <Box sx={{ position: 'relative' }}>
                <Button
                  fullWidth
                  variant="text"
                  disableElevation
                  onClick={() => onSelectCategory(category.category)}
                  sx={{ ...buttonSx, pl: `${INDENT_STEP}px !important`, pr: `${ROW_PADDING_RIGHT}px !important`, fontWeight: isSelected ? 600 : 400, bgcolor: isSelected ? 'grey.200' : 'transparent' }}
                >
                  <span>{category.category}</span>
                </Button>
                <PositionedBadge count={category.unreadCount} />
                {hasSubs ? (
                  <Tooltip title={expanded ? 'Inklappen' : 'Uitklappen'}>
                    <IconButton
                      size="small"
                      onClick={() => toggleCategoryExpanded(category.category)}
                      sx={{
                        width: ARROW_SLOT_WIDTH,
                        height: ARROW_SLOT_WIDTH,
                        borderRadius: '50%',
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <Icon fontSize="small">{expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>

              {expanded && hasSubs && (
                <>
                  <Button
                    fullWidth
                    size="small"
                    variant="text"
                    disableElevation
                    onClick={() => onSelectSubCategory(null)}
                    sx={{ ...buttonSx, pl: `${SUB_LEVEL_INDENT}px !important`, pr: `${ROW_PADDING_RIGHT}px !important`, fontWeight: selectedSubCategory === null ? 600 : 400, bgcolor: selectedSubCategory === null ? 'grey.200' : 'transparent' }}
                  >
                    Alles in {category.category}
                  </Button>

                  {category.subCategories.map((sub) => {
                    const subSelected = selectedSubCategory === sub.subCategory;
                    return (
                      <Box key={sub.subCategory} sx={{ position: 'relative' }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="text"
                          disableElevation
                          onClick={() => onSelectSubCategory(sub.subCategory)}
                          sx={{ ...buttonSx, pl: `${SUB_LEVEL_INDENT}px !important`, pr: `${ROW_PADDING_RIGHT}px !important`, fontWeight: subSelected ? 600 : 400, bgcolor: subSelected ? 'grey.200' : 'transparent' }}
                        >
                          <span>{sub.subCategory}</span>
                        </Button>
                        <PositionedBadge count={sub.unreadCount} />
                      </Box>
                    );
                  })}
                </>
              )}
            </React.Fragment>
          );
        })}

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="overline" color="text.secondary">Datum</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
          <DatePicker
            label="Van"
            value={dateFrom}
            onChange={onChangeDateFrom}
            format="DD/MM/YYYY"
            maxDate={dateTo ?? undefined}
            slotProps={{
              field: { clearable: true },
              textField: { variant: 'filled', fullWidth: true },
            }}
          />
          <DatePicker
            label="Tot en met"
            value={dateTo}
            onChange={onChangeDateTo}
            format="DD/MM/YYYY"
            minDate={dateFrom ?? undefined}
            slotProps={{
              field: { clearable: true },
              textField: { variant: 'filled', fullWidth: true },
            }}
          />
        </Box>

        {organizations.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Organisaties</Typography>
            <FilterCategoryMultiSelect
              label="Organisatie"
              placeholder="Zoek en selecteer één of meerdere organisaties"
              noOptionsText="Geen organisaties beschikbaar"
              items={organisatieItems}
              selectedValues={selectedOrganizations}
              onChange={onChangeOrganizations}
              chipStyleVariant="default"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FilterCategorieen;
